// Authentication synchronization utility for cross-origin auth sharing
// This handles sharing auth state between different localhost ports during development

const AUTH_SYNC_KEY = 'jua-auth-sync';
const STORAGE_EVENT_KEY = 'jua-auth-storage-sync';

interface AuthSyncData {
  session: any;
  timestamp: number;
}

export class AuthSync {
  private static instance: AuthSync;
  private listeners: Set<(session: any) => void> = new Set();
  private broadcastChannel?: BroadcastChannel;

  private constructor() {
    // Use BroadcastChannel if available (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(AUTH_SYNC_KEY);
      this.broadcastChannel.addEventListener('message', (event) => {
        if (event.data.type === 'auth-session-update') {
          this.notifyListeners(event.data.session);
        }
      });
    }

    // Fallback to storage events for cross-origin communication
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_EVENT_KEY && event.newValue) {
        try {
          const data: AuthSyncData = JSON.parse(event.newValue);
          // Only process recent updates (within last 5 seconds)
          if (Date.now() - data.timestamp < 5000) {
            this.notifyListeners(data.session);
          }
        } catch (error) {
          console.warn('Failed to parse auth sync data:', error);
        }
      }
    });
  }

  public static getInstance(): AuthSync {
    if (!AuthSync.instance) {
      AuthSync.instance = new AuthSync();
    }
    return AuthSync.instance;
  }

  public syncSession(session: any): void {
    const data: AuthSyncData = {
      session,
      timestamp: Date.now(),
    };

    // Store in localStorage for cross-origin access
    try {
      localStorage.setItem(STORAGE_EVENT_KEY, JSON.stringify(data));
      // Remove the item immediately to trigger storage event
      localStorage.removeItem(STORAGE_EVENT_KEY);
    } catch (error) {
      console.warn('Failed to sync session via localStorage:', error);
    }

    // Use BroadcastChannel for same-origin communication
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage({
          type: 'auth-session-update',
          session,
        });
      } catch (error) {
        console.warn('Failed to sync session via BroadcastChannel:', error);
      }
    }
  }

  public getStoredSession(): any {
    try {
      const storedSession = localStorage.getItem('supabase.auth.token');
      if (storedSession) {
        return JSON.parse(storedSession);
      }
    } catch (error) {
      console.warn('Failed to get stored session:', error);
    }
    return null;
  }

  public addListener(callback: (session: any) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(session: any): void {
    this.listeners.forEach((callback) => {
      try {
        callback(session);
      } catch (error) {
        console.error('Error in auth sync listener:', error);
      }
    });
  }

  public cleanup(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    this.listeners.clear();
  }
}

// Secure cross-origin session transfer
const AUTH_TRANSFER_KEY = 'jua_auth_transfer';
const TRANSFER_EXPIRY_MS = 30000; // 30 seconds

interface AuthTransferData {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
  timestamp: number;
  nonce: string; // Random value to prevent replay attacks
}

// Generate a secure random nonce
function generateNonce(): string {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36);
}

// Encrypt sensitive data (basic implementation - in production, use proper encryption)
function encodeAuthData(data: AuthTransferData): string {
  try {
    // In a real implementation, use proper encryption here
    // For now, we'll use base64 with some obfuscation
    const jsonString = JSON.stringify(data);
    return btoa(jsonString).replace(/[+/=]/g, (m) => ({ '+': '-', '/': '_', '=': '' })[m] || '');
  } catch (error) {
    console.error('Failed to encode auth data:', error);
    return '';
  }
}

// Decrypt sensitive data
function decodeAuthData(encoded: string): AuthTransferData | null {
  try {
    // Reverse the obfuscation
    const base64 = encoded.replace(/[-_]/g, (m) => ({ '-': '+', _: '/' })[m] || '');
    // Add padding if needed
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const jsonString = atob(padded);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Failed to decode auth data:', error);
    return null;
  }
}

// URL-based session transfer for cross-origin navigation (more secure)
export function createCrossOriginAuthUrl(targetUrl: string, session: any): string {
  if (!session?.access_token) {
    return targetUrl;
  }

  // Create transfer data with expiry and nonce
  const transferData: AuthTransferData = {
    access_token: session.access_token,
    refresh_token: session.refresh_token || '',
    expires_at: session.expires_at,
    timestamp: Date.now(),
    nonce: generateNonce(),
  };

  // For cross-origin (different ports), sessionStorage won't work, so use URL method
  const currentOrigin = window.location.origin;
  const targetOrigin = new URL(targetUrl).origin;
  const isCrossOrigin = currentOrigin !== targetOrigin;

  if (isCrossOrigin) {
    console.log('Cross-origin detected, using URL-based auth transfer');
    // Use URL method for cross-origin
    const encoded = encodeAuthData(transferData);
    const url = new URL(targetUrl);
    url.searchParams.set('auth_transfer', encoded);
    return url.toString();
  }

  // Store in sessionStorage for same-origin (more secure than URL)
  const transferKey = `${AUTH_TRANSFER_KEY}_${transferData.nonce}`;
  try {
    sessionStorage.setItem(transferKey, JSON.stringify(transferData));

    // Clean up after expiry
    setTimeout(() => {
      sessionStorage.removeItem(transferKey);
    }, TRANSFER_EXPIRY_MS);

    // Add only the transfer key to URL
    const url = new URL(targetUrl);
    url.searchParams.set('auth_key', transferData.nonce);
    return url.toString();
  } catch (error) {
    console.warn('Failed to store auth transfer data, falling back to URL method:', error);
    // Fallback to URL method
    const encoded = encodeAuthData(transferData);
    const url = new URL(targetUrl);
    url.searchParams.set('auth_transfer', encoded);
    return url.toString();
  }
}

export function extractSessionFromUrl(): any | null {
  const urlParams = new URLSearchParams(window.location.search);
  console.log('Checking for auth transfer, URL params:', urlParams.toString());

  // Method 1: Try sessionStorage first (same-origin or shared storage)
  const authKey = urlParams.get('auth_key');
  if (authKey) {
    console.log('Found auth_key in URL:', authKey);
    const transferKey = `${AUTH_TRANSFER_KEY}_${authKey}`;
    try {
      const storedData = sessionStorage.getItem(transferKey);
      if (storedData) {
        const transferData: AuthTransferData = JSON.parse(storedData);

        // Validate expiry
        if (Date.now() - transferData.timestamp > TRANSFER_EXPIRY_MS) {
          console.warn('Auth transfer data expired');
          sessionStorage.removeItem(transferKey);
          return null;
        }

        // Clean up
        sessionStorage.removeItem(transferKey);
        urlParams.delete('auth_key');

        const newUrl =
          window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);

        return {
          access_token: transferData.access_token,
          refresh_token: transferData.refresh_token,
          expires_at: transferData.expires_at,
          user: null,
        };
      }
    } catch (error) {
      console.error('Failed to retrieve auth transfer data:', error);
    }
  }

  // Method 2: Fallback to URL-based transfer (less secure, for cross-origin)
  const authTransfer = urlParams.get('auth_transfer');
  if (authTransfer) {
    console.log('Found auth_transfer in URL, decoding...');
    const transferData = decodeAuthData(authTransfer);
    if (transferData) {
      console.log('Successfully decoded auth transfer data');
      // Validate expiry
      if (Date.now() - transferData.timestamp > TRANSFER_EXPIRY_MS) {
        console.warn('URL-based auth transfer data expired');
        return null;
      }

      // Clean up URL
      urlParams.delete('auth_transfer');
      const newUrl =
        window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);

      return {
        access_token: transferData.access_token,
        refresh_token: transferData.refresh_token,
        expires_at: transferData.expires_at,
        user: null,
      };
    }
  }

  return null;
}
